import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiPlay, FiEye, FiDatabase } from 'react-icons/fi';
import Portal from '../Portal';

const ActionConfiguration = () => {
  const [actionConfigs, setActionConfigs] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddAction, setShowAddAction] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [showExecutions, setShowExecutions] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [actionResponse, endpointsResponse] = await Promise.all([
        adminAPI.getActionConfigs(),
        adminAPI.getEndpoints()
      ]);
      setActionConfigs(actionResponse.data.actionConfigs);
      setEndpoints(endpointsResponse.data.endpoints);
    } catch (error) {
      showMessage('Failed to load action configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadExecutions = async () => {
    try {
      const response = await adminAPI.getActionExecutions();
      setExecutions(response.data.executions);
      setShowExecutions(true);
    } catch (error) {
      showMessage('Failed to load execution history', 'error');
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm('Are you sure you want to delete this action configuration? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteActionConfig(actionId);
      setActionConfigs(actionConfigs.filter(action => action.id !== actionId));
      showMessage('Action configuration deleted successfully', 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete action configuration', 'error');
    }
  };

  const filteredActions = actionConfigs.filter(action =>
    action.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    action.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    action.endpointName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading action configurations...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Action Configuration</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => {
              if (showExecutions) {
                setShowExecutions(false);
              } else {
                loadExecutions();
              }
            }}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <FiEye style={{ marginRight: '5px' }} />
            {showExecutions ? 'Hide' : 'View'} Executions
          </button>
          <button
            onClick={() => setShowAddAction(true)}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <FiPlus style={{ marginRight: '5px' }} />
            Add Action Config
          </button>
        </div>
      </div>

      {message && (
        <div 
          className={messageType === 'error' ? 'error-message' : 'success-message'}
          style={{ marginBottom: '20px' }}
        >
          {message}
        </div>
      )}

      {showExecutions && (
        <div className="card" style={{ marginBottom: '30px' }}>
          <div className="card-header">
            <h2 className="card-title">Action Execution History ({executions.length})</h2>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>User</th>
                  <th>Form Data</th>
                  <th>Status</th>
                  <th>Executed At</th>
                </tr>
              </thead>
              <tbody>
                {executions.map(execution => (
                  <tr key={execution.id}>
                    <td>{execution.actionName}</td>
                    <td>{execution.username}</td>
                    <td>
                      <code style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '2px 6px', 
                        borderRadius: '3px',
                        fontSize: '12px'
                      }}>
                        {JSON.stringify(execution.formData)}
                      </code>
                    </td>
                    <td>
                      <span 
                        className={`badge ${execution.status === 'success' ? 'badge-success' : 'badge-danger'}`}
                      >
                        {execution.status}
                      </span>
                    </td>
                    <td>{new Date(execution.executedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {executions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No action executions found.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Action Configurations ({filteredActions.length})</h2>
            <div style={{ position: 'relative', width: '300px' }}>
              <FiSearch style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#666'
              }} />
              <input
                type="text"
                placeholder="Search action configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: '35px' }}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Endpoint</th>
                <th>Form Fields</th>
                <th>Button Text</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActions.map(action => (
                <tr key={action.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiPlay style={{ marginRight: '8px', color: '#007bff' }} />
                      <strong>{action.name}</strong>
                    </div>
                  </td>
                  <td>{action.description || 'No description'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiDatabase style={{ marginRight: '5px', color: '#6c757d' }} />
                      {action.endpointName || `Endpoint ${action.endpointId}`}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {action.formFields?.length || 0} fields
                    </span>
                  </td>
                  <td>
                    <span style={{ 
                      backgroundColor: '#e9ecef', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {action.buttonText}
                    </span>
                  </td>
                  <td>
                    <span 
                      className={`badge ${action.isActive ? 'badge-success' : 'badge-secondary'}`}
                    >
                      {action.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => setEditingAction(action)}
                        className="btn btn-small btn-secondary"
                        title="Edit Action Configuration"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDeleteAction(action.id)}
                        className="btn btn-small btn-danger"
                        title="Delete Action Configuration"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredActions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {searchTerm ? 'No action configurations found matching your search.' : 'No action configurations found.'}
              {!searchTerm && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => setShowAddAction(true)}
                    className="btn btn-primary"
                  >
                    Create Your First Action Configuration
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddAction && (
        <Portal>
          <ActionConfigModal
            endpoints={endpoints}
            onClose={() => setShowAddAction(false)}
            onActionAdded={(newAction) => {
              setActionConfigs([...actionConfigs, newAction]);
              setShowAddAction(false);
              showMessage('Action configuration created successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}

      {editingAction && (
        <Portal>
          <ActionConfigModal
            action={editingAction}
            endpoints={endpoints}
            onClose={() => setEditingAction(null)}
            onActionUpdated={(updatedAction) => {
              setActionConfigs(actionConfigs.map(action => 
                action.id === updatedAction.id ? updatedAction : action
              ));
              setEditingAction(null);
              showMessage('Action configuration updated successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}
    </div>
  );
};

const ActionConfigModal = ({ action, endpoints, onClose, onActionAdded, onActionUpdated, onError }) => {
  const [formData, setFormData] = useState({
    name: action?.name || '',
    description: action?.description || '',
    endpointId: action?.endpointId || '',
    formFields: action?.formFields || [],
    buttonText: action?.buttonText || 'Execute',
    confirmationMessage: action?.confirmationMessage || ''
  });
  const [loading, setLoading] = useState(false);

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'tel', label: 'Phone' },
    { value: 'url', label: 'URL' },
    { value: 'textarea', label: 'Textarea' }
  ];

  const addFormField = () => {
    setFormData({
      ...formData,
      formFields: [
        ...formData.formFields,
        {
          name: '',
          type: 'text',
          label: '',
          placeholder: '',
          required: false,
          pattern: ''
        }
      ]
    });
  };

  const updateFormField = (index, field, value) => {
    const updatedFields = [...formData.formFields];
    updatedFields[index] = { ...updatedFields[index], [field]: value };
    setFormData({ ...formData, formFields: updatedFields });
  };

  const removeFormField = (index) => {
    const updatedFields = formData.formFields.filter((_, i) => i !== index);
    setFormData({ ...formData, formFields: updatedFields });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData
      };

      if (action) {
        const response = await adminAPI.updateActionConfig(action.id, payload);
        onActionUpdated(response.data.actionConfig || { ...action, ...payload });
      } else {
        const response = await adminAPI.createActionConfig(payload);
        onActionAdded(response.data.actionConfig || { id: Date.now(), ...payload });
      }
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to save action configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>{action ? 'Edit Action Configuration' : 'Add New Action Configuration'}</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Action Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="form-input"
              required
              placeholder="e.g., Add IP to Whitelist"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="form-input"
              rows="2"
              placeholder="Brief description of this action"
            />
          </div>

          <div className="form-group">
            <label className="form-label">API Endpoint *</label>
            <select
              value={formData.endpointId}
              onChange={(e) => setFormData({...formData, endpointId: e.target.value})}
              className="form-input"
              required
            >
              <option value="">Select an endpoint</option>
              {endpoints.map(endpoint => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.name} ({endpoint.method} {endpoint.url})
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Button Text</label>
              <input
                type="text"
                value={formData.buttonText}
                onChange={(e) => setFormData({...formData, buttonText: e.target.value})}
                className="form-input"
                placeholder="Execute"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirmation Message</label>
              <input
                type="text"
                value={formData.confirmationMessage}
                onChange={(e) => setFormData({...formData, confirmationMessage: e.target.value})}
                className="form-input"
                placeholder="Are you sure?"
              />
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <label className="form-label" style={{ margin: 0 }}>Form Fields</label>
              <button
                type="button"
                onClick={addFormField}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiPlus style={{ marginRight: '5px' }} />
                Add Field
              </button>
            </div>

            {formData.formFields.map((field, index) => (
              <div key={index} style={{ 
                border: '1px solid #dee2e6', 
                borderRadius: '8px',
                padding: '20px', 
                marginBottom: '15px', 
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, color: '#333' }}>Field {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeFormField(index)}
                    className="btn btn-small btn-danger"
                    title="Remove Field"
                  >
                    <FiTrash2 />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label className="form-label">Field Name *</label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateFormField(index, 'name', e.target.value)}
                      className="form-input"
                      placeholder="e.g., ipAddress"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Field Type *</label>
                    <select
                      value={field.type}
                      onChange={(e) => updateFormField(index, 'type', e.target.value)}
                      className="form-input"
                      required
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label className="form-label">Label *</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateFormField(index, 'label', e.target.value)}
                      className="form-input"
                      placeholder="e.g., IP Address"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Placeholder</label>
                    <input
                      type="text"
                      value={field.placeholder}
                      onChange={(e) => updateFormField(index, 'placeholder', e.target.value)}
                      className="form-input"
                      placeholder="e.g., 192.168.1.1"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '15px', alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateFormField(index, 'required', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      <span className="form-label" style={{ margin: 0 }}>Required Field</span>
                    </label>
                  </div>
                  <div>
                    <label className="form-label">Validation Pattern (Regex)</label>
                    <input
                      type="text"
                      value={field.pattern}
                      onChange={(e) => updateFormField(index, 'pattern', e.target.value)}
                      className="form-input"
                      placeholder="e.g., ^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}$ for IP"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Optional: Regular expression for field validation
                    </small>
                  </div>
                </div>
              </div>
            ))}

            {formData.formFields.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                color: '#666',
                border: '2px dashed #dee2e6',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 15px 0' }}>No form fields added yet.</p>
                <button
                  type="button"
                  onClick={addFormField}
                  className="btn btn-primary"
                >
                  Add Your First Field
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : (action ? 'Update Action' : 'Create Action')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActionConfiguration;
